export type SupportedLanguage = 'zh-CN' | 'zh-TW' | 'en' | 'ja' | 'ko';

type TextDirection = 'ltr' | 'rtl';

export interface LanguageConfig {
  name: string;
  nativeName: string;
  flag: string;
  direction: TextDirection;
}

export const LANGUAGE_CONFIG: Record<SupportedLanguage, LanguageConfig> = {
  'zh-CN': {
    name: '简体中文',
    nativeName: '简体中文',
    flag: '🇨🇳',
    direction: 'ltr',
  },
  'zh-TW': {
    name: '繁體中文',
    nativeName: '繁體中文',
    flag: '🇹🇼',
    direction: 'ltr',
  },
  en: {
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    direction: 'ltr',
  },
  ja: {
    name: '日本語',
    nativeName: '日本語',
    flag: '🇯🇵',
    direction: 'ltr',
  },
  ko: {
    name: '한국어',
    nativeName: '한국어',
    flag: '🇰🇷',
    direction: 'ltr',
  },
};

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = Object.keys(LANGUAGE_CONFIG) as SupportedLanguage[];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === 'string' && SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);

/**
 * 根据任意语种字符串（包含区域标识）推断支持的语言代码。
 * 例如：zh-Hant → zh-TW, zh-CN → zh-CN, en-US → en。
 */
export const normalizeLanguage = (language?: string | null): SupportedLanguage => {
  if (!language) {
    return DEFAULT_LANGUAGE;
  }

  if (isSupportedLanguage(language)) {
    return language;
  }

  const lower = language.toLowerCase();

  if (lower.startsWith('zh-tw') || lower.includes('zh_hant') || lower.includes('zh-hant')) {
    return 'zh-TW';
  }

  if (lower.startsWith('zh')) {
    return 'zh-CN';
  }

  if (lower.startsWith('ja')) {
    return 'ja';
  }

  if (lower.startsWith('ko')) {
    return 'ko';
  }

  if (lower.startsWith('en')) {
    return 'en';
  }

  return DEFAULT_LANGUAGE;
};

/**
 * 解析环境变量或用户输入的语言值，返回支持的语言代码。
 * 允许传入 SupportedLanguage 或任意字符串。
 */
export const resolveLanguage = (language?: string | SupportedLanguage | null): SupportedLanguage => {
  if (!language) {
    return DEFAULT_LANGUAGE;
  }

  if (isSupportedLanguage(language)) {
    return language;
  }

  return normalizeLanguage(language);
};

export const getLanguageConfig = (language: SupportedLanguage): LanguageConfig => LANGUAGE_CONFIG[language];

/**
 * 获取当前语言设置（平台相关）
 * 此函数应该在各平台实现，这里提供默认实现
 * 在 Web 环境中，从 localStorage 读取
 * 在 Mobile 环境中，从 AsyncStorage 读取
 */
export const getLanguage = async (): Promise<SupportedLanguage> => {
  // Web 环境默认实现
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const stored = window.localStorage.getItem('cubemeet-language');
      if (stored && isSupportedLanguage(stored)) {
        return stored;
      }
    } catch (error) {
      console.warn('Failed to read language from localStorage:', error);
    }
  }
  
  // 返回默认语言
  return DEFAULT_LANGUAGE;
};
